import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Search, ChevronRight, User2, Bell,
  ArrowUp, ArrowDown, ArrowUpDown,
} from "lucide-react";
import Layout from "@/components/Layout";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { parsePhoneNumberFromString } from "libphonenumber-js";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [clientsWithSales, setClientsWithSales] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [onlyFollowup, setOnlyFollowup] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", age: "", notes: "", telefono: "", email: "" });
  const [phoneError, setPhoneError] = useState("");
  const [saving, setSaving] = useState(false);
  const [sortField, setSortField] = useState(null); // 'name' | 'age' | 'analyses_count' | 'created_at' | 'status'
  const [sortDir, setSortDir] = useState("asc");

  const fetchAll = async () => {
    // Usiamo Promise.allSettled per essere difensivi: se una delle 2 chiamate
    // fallisce (timeout, 5xx, rete), NON azzeriamo gli altri state. Il
    // principio è: mostrare sempre ciò che riusciamo a caricare, mai un
    // crash totale della pagina.
    const [cRes, ssRes] = await Promise.allSettled([
      api.get("/clients"),
      api.get("/clients/sales-summary"),
    ]);

    if (cRes.status === "fulfilled") {
      setClients(cRes.value.data);
    } else {
      // NON sovrascrivere `clients` se già popolato da un render precedente.
      console.warn("Clients: impossibile ricaricare i clienti", cRes.reason?.message);
    }

    if (ssRes.status === "fulfilled") {
      setClientsWithSales(new Set(ssRes.value.data.client_ids_with_sales || []));
    }

    if (cRes.status === "rejected" && ssRes.status === "rejected") {
      toast.error("Connessione al server non disponibile. Riprova.");
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setPhoneError("");
    // Phone validation: optional, but if provided MUST be a valid international number.
    let telE164 = null;
    const rawTel = (form.telefono || "").trim();
    if (rawTel) {
      const parsed = parsePhoneNumberFromString(rawTel, "IT");
      if (!parsed || !parsed.isValid()) {
        setPhoneError("Numero non valido. Usa il formato internazionale (es. +39 333 1234567).");
        return;
      }
      telE164 = parsed.number; // E.164 format with leading "+"
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        age: form.age ? parseInt(form.age, 10) : null,
        notes: form.notes.trim() || null,
        telefono: telE164,
        email: form.email.trim().toLowerCase() || null,
      };
      await api.post("/clients", payload);
      toast.success("Cliente creato");
      setOpen(false);
      setForm({ name: "", age: "", notes: "", telefono: "", email: "" });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const toggleSort = (field) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortField(null);
      setSortDir("asc");
    }
  };

  const filtered = (() => {
    const base = clients.filter(c => {
      if (onlyFollowup && !c.needs_followup) return false;
      return c.name.toLowerCase().includes(search.toLowerCase());
    });
    if (!sortField) return base;
    const dir = sortDir === "asc" ? 1 : -1;
    const sorted = [...base].sort((a, b) => {
      let va, vb;
      switch (sortField) {
        case "name":           va = (a.name || "").toLowerCase(); vb = (b.name || "").toLowerCase(); break;
        case "age":            va = a.age ?? -1;                  vb = b.age ?? -1; break;
        case "analyses_count": va = a.analyses_count ?? 0;        vb = b.analyses_count ?? 0; break;
        case "created_at":     va = a.created_at || "";           vb = b.created_at || ""; break;
        case "status":         va = a.needs_followup ? 1 : 0;     vb = b.needs_followup ? 1 : 0; break;
        default:               va = 0;                            vb = 0;
      }
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return sorted;
  })();
  const followupCount = clients.filter(c => c.needs_followup).length;

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  return (
    <Layout>
      {/* Hero */}
      <div className="mb-10 animate-fade-up">
        <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">Clienti</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] mb-3">
          I tuoi clienti
        </h1>
        <p className="text-[#737373] max-w-xl">
          Cerca e gestisci le schede dei tuoi clienti. Ogni scheda contiene lo storico completo dei protocolli generati.
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca cliente..."
            className="w-full h-12 pl-12 pr-4 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none"
            data-testid="search-clients-input"
          />
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              className="h-12 px-6 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium transition-all duration-300 flex items-center justify-center gap-2"
              data-testid="new-client-button"
            >
              <Plus className="w-4 h-4" />
              Nuovo cliente
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-3xl">Nuovo cliente</DialogTitle>
              <DialogDescription className="text-sm text-[#737373]">
                Inserisci i dati di contatto: il telefono e l'email abilitano la condivisione del report via WhatsApp ed Email.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 pt-2" data-testid="new-client-form">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Nome completo *</label>
                <input
                  required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none"
                  data-testid="client-name-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Età</label>
                <input
                  type="number" min="0" max="120" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none"
                  data-testid="client-age-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Telefono</label>
                <input
                  type="tel" value={form.telefono}
                  onChange={(e) => { setForm({ ...form, telefono: e.target.value }); if (phoneError) setPhoneError(""); }}
                  placeholder="+39 333 1234567"
                  className={`w-full h-12 px-4 rounded-xl border bg-white focus:ring-2 outline-none ${
                    phoneError
                      ? "border-[#D9534F] focus:border-[#D9534F] focus:ring-[#D9534F]/20"
                      : "border-[#E5E5E0] focus:border-[#C5A059] focus:ring-[#C5A059]/20"
                  }`}
                  data-testid="client-phone-input"
                />
                {phoneError ? (
                  <p className="text-xs text-[#D9534F] mt-1.5" data-testid="client-phone-error">{phoneError}</p>
                ) : (
                  <p className="text-xs text-[#A3A3A3] mt-1.5">Formato internazionale, abilita la condivisione su WhatsApp.</p>
                )}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Email</label>
                <input
                  type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="cliente@email.com"
                  className="w-full h-12 px-4 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none"
                  data-testid="client-email-input"
                />
                <p className="text-xs text-[#A3A3A3] mt-1.5">Abilita l'invio del report tramite Email.</p>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Note</label>
                <textarea
                  rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Storico, allergie, preferenze..."
                  className="w-full px-4 py-3 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none resize-none"
                  data-testid="client-notes-input"
                />
              </div>
              <DialogFooter>
                <button
                  type="submit" disabled={saving}
                  className="h-11 px-6 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium transition-all disabled:opacity-50"
                  data-testid="client-save-button"
                >
                  {saving ? "Salvataggio..." : "Salva cliente"}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Follow-up filter chip — visible only when at least one client needs it */}
      {followupCount > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="followup-filter-row">
          <button
            type="button"
            onClick={() => setOnlyFollowup(v => !v)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
              onlyFollowup
                ? "bg-[#1A1A1A] border-[#1A1A1A] text-white"
                : "bg-[#FAF6EE] border-[#C5A059]/40 text-[#8A6B2E] hover:bg-[#F3E8CB]"
            }`}
            data-testid="followup-filter-toggle"
          >
            <Bell className="w-3.5 h-3.5" />
            {onlyFollowup ? "Mostra tutti" : `Solo da contattare · ${followupCount}`}
          </button>
          {onlyFollowup && (
            <span className="text-xs text-[#737373]">
              Clienti con ultima analisi oltre 30 giorni fa
            </span>
          )}
        </div>
      )}

      {/* Clients table */}
      {loading ? (
        <div className="rounded-2xl border border-[#E5E5E0] bg-white overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-16 shimmer border-b border-[#E5E5E0] last:border-b-0" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState hasSearch={!!search} onNew={() => setOpen(true)} />
      ) : (
        <div className="rounded-2xl border border-[#E5E5E0] bg-white overflow-hidden" data-testid="clients-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] text-[11px] uppercase tracking-wider text-[#737373]">
                <tr>
                  <th className="text-left px-6 py-3 font-medium">
                    <button onClick={() => toggleSort("name")} className="inline-flex items-center gap-1.5 hover:text-[#1A1A1A] transition-colors" data-testid="sort-name">
                      Nome <SortIcon field="name" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 font-medium">
                    <button onClick={() => toggleSort("age")} className="inline-flex items-center gap-1.5 hover:text-[#1A1A1A] transition-colors" data-testid="sort-age">
                      Età <SortIcon field="age" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 font-medium">Contatti</th>
                  <th className="text-left px-4 py-3 font-medium">
                    <button onClick={() => toggleSort("analyses_count")} className="inline-flex items-center gap-1.5 hover:text-[#1A1A1A] transition-colors" data-testid="sort-analyses">
                      Analisi <SortIcon field="analyses_count" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 font-medium">
                    <button onClick={() => toggleSort("status")} className="inline-flex items-center gap-1.5 hover:text-[#1A1A1A] transition-colors" data-testid="sort-status">
                      Stato <SortIcon field="status" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 font-medium">
                    <button onClick={() => toggleSort("created_at")} className="inline-flex items-center gap-1.5 hover:text-[#1A1A1A] transition-colors" data-testid="sort-created">
                      Iscrizione <SortIcon field="created_at" />
                    </button>
                  </th>
                  <th className="text-right px-6 py-3 font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E0]">
                {filtered.map((c) => {
                  const hasSales = clientsWithSales.has(c.id);
                  return (
                    <tr
                      key={c.id}
                      className="hover:bg-[#FAFAFA] transition-colors"
                      data-testid={`client-row-${c.id}`}
                      data-has-sales={hasSales ? "true" : "false"}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                            hasSales ? "bg-emerald-100" : "bg-[#EAE5DF]"
                          }`}>
                            <User2 className={`w-4 h-4 ${hasSales ? "text-emerald-600" : "text-[#C5A059]"}`} />
                          </div>
                          <p className="text-[#1A1A1A] font-medium">{c.name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-[#1A1A1A]">{c.age ? `${c.age} anni` : "—"}</td>
                      <td className="px-4 py-4 text-xs text-[#737373]">
                        {c.telefono || c.email ? (
                          <div className="flex flex-col gap-0.5">
                            {c.telefono && <span>{c.telefono}</span>}
                            {c.email && <span>{c.email}</span>}
                          </div>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-4 text-[#1A1A1A]">{c.analyses_count}</td>
                      <td className="px-4 py-4">
                        {c.needs_followup ? (
                          <span
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FAF6EE] border border-[#C5A059]/50 text-[#8A6B2E] text-[10px] font-semibold"
                            data-testid={`client-needs-followup-${c.id}`}
                            title={`Ultima analisi ${c.days_since_last_analysis} giorni fa`}
                          >
                            <Bell className="w-3 h-3" />
                            Da contattare
                          </span>
                        ) : hasSales ? (
                          <span className="inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Cliente attivo
                          </span>
                        ) : (
                          <span className="text-xs text-[#A3A3A3]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-[#737373] text-xs">
                        {new Date(c.created_at).toLocaleDateString("it-IT")}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end">
                          <Link
                            to={`/client/${c.id}`}
                            className="h-8 px-3 rounded-lg border border-[#E5E5E0] hover:border-[#C5A059] text-[#1A1A1A] text-xs font-medium inline-flex items-center gap-1.5 transition-all"
                            data-testid={`client-view-${c.id}`}
                          >
                            Apri <ChevronRight className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Layout>
  );
}

function EmptyState({ hasSearch, onNew }) {
  if (hasSearch) {
    return (
      <div className="text-center py-20 animate-fade-in">
        <p className="font-serif-display text-2xl text-[#1A1A1A] mb-2">Nessun risultato</p>
        <p className="text-[#737373]">Prova a cercare con un altro nome.</p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-dashed border-[#E5E5E0] p-16 text-center animate-fade-in" data-testid="empty-state">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-[#EAE5DF] flex items-center justify-center mb-6">
        <User2 className="w-7 h-7 text-[#C5A059]" />
      </div>
      <h3 className="font-serif-display text-3xl text-[#1A1A1A] mb-2">Il tuo studio ti aspetta</h3>
      <p className="text-[#737373] mb-8 max-w-md mx-auto">
        Inizia creando la scheda del tuo primo cliente. Potrai poi generare analisi tricologiche con AI e protocolli personalizzati.
      </p>
      <button
        onClick={onNew}
        className="h-12 px-6 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium transition-all inline-flex items-center gap-2"
        data-testid="empty-new-client-button"
      >
        <Plus className="w-4 h-4" />
        Crea primo cliente
      </button>
    </div>
  );
}
